import { BaseSyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  WorkflowDataType,
  WorkflowScopeType,
  WorkflowTriggerType,
} from '@invenco/common-domain/enums';
import { RuleDTO, WorkflowDTO } from '@invenco/common-interface/automation';

import { Result } from '@/shared/helpers/Result';
import { useStateWithRef } from '@/shared/hooks/use-state-with-ref';
import { useMessages } from '@/shared/providers/MessagesProvider';

import type { WorkflowFormValues } from '../workflow-form-fields';
import {
  appendAction,
  removeAction as removeActionFromRule,
  revealActions,
} from './action-panel/action-draft';
import { revealLeaves } from './condition-panel/condition-draft';
import { EditorAction, EditorActionBranch, EditorRule } from './editor-model';
import { denormalizeRules, normalizeRules } from './normalize';
import {
  firstInvalidRuleIndex,
  hasWorkflowErrors,
  validateWorkflow,
  type WorkflowErrors,
} from './validate-workflow';

/** The slice of a workflow the editor needs; callers without one supply defaults. */
export type WorkflowEditorInput = Pick<WorkflowDTO, 'triggerType' | 'dataType' | 'rules'>;

/** The currently selected canvas node; the trigger pill is not selectable. */
export type WorkflowEditorSelection =
  | { kind: 'condition'; ruleIndex: number }
  | { kind: 'action'; ruleIndex: number; branch: EditorActionBranch; actionIndex: number }
  | null;

/** Read-only render context shared by every node on the canvas. */
export interface WorkflowEditorContext {
  triggerType: WorkflowTriggerType;
  dataType: WorkflowDataType;
  scopeType: WorkflowScopeType;
}

export interface UseWorkflowEditorStateOptions {
  workflow: WorkflowEditorInput;
  scopeType: WorkflowScopeType;
  /**
   * The caller-owned metadata form; the session drives its submit, folds its
   * dirty state into `isDirty`, and resets it on successful save.
   */
  form: UseFormReturn<WorkflowFormValues>;
  /**
   * Persists the validated metadata values and the denormalized rules; must
   * report failure as `Result.fail` — the dirty baseline only advances on success.
   */
  onSave?: (values: WorkflowFormValues, rules: RuleDTO[]) => Promise<Result>;
}

/**
 * The editor instance consumed by `WorkflowCanvas` and `WorkflowEditorPanel`.
 * Owns the whole draft session (rules working copy, selection, the save over
 * the caller's form); edits stage synchronously and nothing persists until
 * `submit`.
 */
export interface WorkflowEditorState {
  context: WorkflowEditorContext;
  /** The working copy; an empty workflow materializes one placeholder rule. */
  rules: EditorRule[];
  selection: WorkflowEditorSelection;
  select: (next: WorkflowEditorSelection) => void;
  /** Stages an edit to one rule: replaces `rules[ruleIndex]` with `recipe(rules[ruleIndex])`. */
  updateRule: (ruleIndex: number, recipe: (rule: EditorRule) => EditorRule) => void;
  /** Appends a new typeless action to a branch and selects it (see `isConfiguredAction`). */
  addAction: (ruleIndex: number, branch: EditorActionBranch) => void;
  /** Stages an edit to one action: replaces it with `recipe(action)`. */
  updateAction: (
    ruleIndex: number,
    branch: EditorActionBranch,
    actionIndex: number,
    recipe: (action: EditorAction) => EditorAction,
  ) => void;
  /** Removes the action (staged) and deselects it; the panel confirms first — staging has no undo. */
  removeAction: (ruleIndex: number, branch: EditorActionBranch, actionIndex: number) => void;
  /** Rules-only validation, keyed by node identity; metadata errors stay on the form. */
  errors: WorkflowErrors;
  /** True when the metadata form or the rules working copy differs from its baseline. */
  isDirty: boolean;
  isSaving: boolean;
  /**
   * The top-of-page Save: an invalid submit reveals errors and selects the
   * first bad node, a valid dirty one persists the whole draft, a clean click
   * is a no-op. Works as a button `onClick` or a form `onSubmit`.
   */
  submit: (event?: BaseSyntheticEvent) => Promise<void>;
}

/**
 * Serialized form for the dirty compare — denormalized so editor-only ids and
 * the still-empty placeholder don't count as changes.
 */
const serializeRules = (rules: EditorRule[]): string => JSON.stringify(denormalizeRules(rules));

/** Creates the editor's draft session — see {@link WorkflowEditorState}. */
export function useWorkflowEditorState(
  options: UseWorkflowEditorStateOptions,
): WorkflowEditorState {
  const { workflow, scopeType, form, onSave } = options;
  const messages = useMessages();

  const [rules, setRules] = useState<EditorRule[]>(() => normalizeRules(workflow.rules));
  // The dirty baseline, pre-serialized; changes only on seed/save.
  const [baseline, setBaseline] = useState<string>(() => serializeRules(rules));
  const [selection, setSelection] = useState<WorkflowEditorSelection>(null);
  const [isSaving, setIsSaving, isSavingRef] = useStateWithRef(false);

  const errors = useMemo(() => validateWorkflow(rules), [rules]);

  const rulesDirty = useMemo(() => serializeRules(rules) !== baseline, [rules, baseline]);

  // Re-seed when the source rules change content (async load, post-save
  // refetch) — but never while the working copy is dirty, so a refetch can't
  // clobber unsaved edits. Compared serialized so an equal rebuilt array
  // doesn't loop.
  const seededRef = useRef<string>();
  if (seededRef.current === undefined) {
    seededRef.current = JSON.stringify(workflow.rules);
  }
  useEffect(() => {
    if (rulesDirty) {
      return;
    }
    const incoming = JSON.stringify(workflow.rules);
    if (incoming === seededRef.current) {
      return;
    }
    seededRef.current = incoming;
    const seeded = normalizeRules(workflow.rules);
    setRules(seeded);
    setBaseline(serializeRules(seeded));
    setSelection(null);
  }, [workflow.rules, rulesDirty]);

  // The committed rules, readable from the stable-identity `addAction` without
  // churning it per keystroke.
  const rulesRef = useRef(rules);
  rulesRef.current = rules;

  const updateRule = useCallback((ruleIndex: number, recipe: (rule: EditorRule) => EditorRule) => {
    setRules((prev) => prev.map((rule, index) => (index === ruleIndex ? recipe(rule) : rule)));
  }, []);

  const addAction = useCallback((ruleIndex: number, branch: EditorActionBranch) => {
    // The new card's index is needed synchronously for the selection; a
    // `setRules` updater would supply it too late.
    const actionIndex = rulesRef.current[ruleIndex][branch].length;
    setRules((prev) =>
      prev.map((rule, index) => (index === ruleIndex ? appendAction(rule, branch) : rule)),
    );
    setSelection({ kind: 'action', ruleIndex, branch, actionIndex });
  }, []);

  const updateAction = useCallback(
    (
      ruleIndex: number,
      branch: EditorActionBranch,
      actionIndex: number,
      recipe: (action: EditorAction) => EditorAction,
    ) => {
      setRules((prev) =>
        prev.map((rule, index) =>
          index === ruleIndex
            ? {
                ...rule,
                [branch]: rule[branch].map((action, i) =>
                  i === actionIndex ? recipe(action) : action,
                ),
              }
            : rule,
        ),
      );
    },
    [],
  );

  const removeAction = useCallback(
    (ruleIndex: number, branch: EditorActionBranch, actionIndex: number) => {
      setRules((prev) =>
        prev.map((rule, index) =>
          index === ruleIndex ? removeActionFromRule(rule, branch, actionIndex) : rule,
        ),
      );
      setSelection(null);
    },
    [],
  );

  const isDirty = form.formState.isDirty || rulesDirty;

  const submit = useCallback(
    (event?: Parameters<WorkflowEditorState['submit']>[0]) => {
      if (isSavingRef.current) {
        event?.preventDefault();
        return Promise.resolve();
      }

      // Rules validation runs before RHF and the dirty skip: a clean-but-invalid
      // draft must still block, not silently no-op. Node selection waits for
      // the metadata-valid branch so a metadata error keeps RHF's focus in the
      // form.
      const rulesInvalid = hasWorkflowErrors(errors);

      // A failed submit ends every fresh leaf's and action's grace period.
      if (rulesInvalid) {
        setRules((prev) => prev.map((rule) => revealActions(revealLeaves(rule))));
      }

      return form.handleSubmit(
        async (values) => {
          // Concurrent validations can both pass the guard above before either
          // valid callback runs, so lock again at the persistence boundary.
          if (isSavingRef.current) {
            return;
          }
          if (rulesInvalid) {
            setSelection({ kind: 'condition', ruleIndex: firstInvalidRuleIndex(errors) ?? 0 });
            messages.error('Complete or remove the highlighted rules');
            return;
          }
          // Save is always enabled, so a clean click is reachable — make it a no-op.
          if (!onSave || !isDirty) {
            return;
          }
          setIsSaving(true);
          try {
            const payload = denormalizeRules(rules);
            const result = await onSave(values, payload);
            // Advance the baselines only on success; a failed save must not look saved.
            if (result.isSuccess) {
              setBaseline(JSON.stringify(payload));
              form.reset(values);
            }
          } finally {
            setIsSaving(false);
          }
        },
        () => setSelection(null),
      )(event);
    },
    [form, onSave, messages, rules, errors, isDirty, isSavingRef, setIsSaving],
  );

  return useMemo<WorkflowEditorState>(
    () => ({
      context: {
        triggerType: workflow.triggerType,
        dataType: workflow.dataType,
        scopeType,
      },
      rules,
      selection,
      select: setSelection,
      updateRule,
      addAction,
      updateAction,
      removeAction,
      errors,
      isDirty,
      isSaving,
      submit,
    }),
    [
      workflow.triggerType,
      workflow.dataType,
      scopeType,
      rules,
      selection,
      updateRule,
      addAction,
      updateAction,
      removeAction,
      errors,
      isDirty,
      isSaving,
      submit,
    ],
  );
}
